import type {
  MessageAuthorKind,
  PortalThread,
  ThreadKind,
  ThreadPriority,
  ThreadStatus,
} from '@central/api';
import type { StatusTone } from '@central/ui';

/** Kinds a customer may open: every kind, a hand-reported crash included. */
export type CustomerThreadKind = ThreadKind;

export const kindChip: Record<ThreadKind, { tone: StatusTone; label: string }> = {
  question: { tone: 'unk', label: 'Question' },
  renewal: { tone: 'blu', label: 'Renewal' },
  license: { tone: 'blu', label: 'License' },
  bug: { tone: 'warn', label: 'Bug' },
  feature: { tone: 'vio', label: 'Feature' },
  crash: { tone: 'down', label: 'Crash report' },
};

/** A thread's chip: a thread Rethink opened reads "From Rethink" whatever its kind. */
export function threadChip(t: Pick<PortalThread, 'kind' | 'startedBy'>): {
  tone: StatusTone;
  label: string;
} {
  return t.startedBy === 'staff' ? { tone: 'unk', label: 'From Rethink' } : kindChip[t.kind];
}

/** Kinds a customer may open, in form order. */
export const newThreadKinds: Array<{ value: CustomerThreadKind; label: string }> = [
  { value: 'question', label: 'Question' },
  { value: 'renewal', label: 'Renewal' },
  { value: 'license', label: 'License change' },
  { value: 'bug', label: 'Bug' },
  { value: 'feature', label: 'Feature idea' },
  { value: 'crash', label: 'Something crashed' },
];

/** The parts of GoATMS a message can be about; the chosen label is stored as the thread's area. */
export const threadAreas = [
  'Signals · timing plans',
  'Signals · controllers and communication',
  'Detection and counts',
  'Message signs',
  'Cameras and video',
  'Incidents and events',
  'Ramp meters',
  'Tunnels',
  'Maps and road network',
  'Reports',
  'Users and sign-in',
  'Licenses and seats',
  'Something else',
];

export const statusLabel: Record<ThreadStatus, string> = {
  open: 'Open',
  waiting_on_us: 'Waiting on Rethink',
  waiting_on_customer: 'Waiting on you',
  on_hold: 'On hold (waiting on a third party)',
  closed: 'Resolved',
};

/** A status chip's tone: amber while the customer is waited on, green once resolved. */
export const statusTone: Record<ThreadStatus, StatusTone> = {
  open: 'blu',
  waiting_on_us: 'blu',
  waiting_on_customer: 'warn',
  on_hold: 'unk',
  closed: 'ok',
};

/** Every status but closed: what the Open filter lists. */
export const activeStatuses = 'open,waiting_on_us,waiting_on_customer,on_hold';

export const priorityLabel: Record<ThreadPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

/** Urgencies a customer may choose for a new message, least urgent first. */
export const urgencyOptions: Array<{ value: ThreadPriority; label: string }> = [
  { value: 'low', label: 'Low: whenever you can' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High: it is affecting our work' },
  { value: 'urgent', label: 'Urgent: operations are down' },
];

/** Who wrote a message, from the customer's side. */
export function authorLabel(a: { kind: MessageAuthorKind; name?: string }): string {
  if (a.kind === 'system') return 'Crash report';
  if (a.kind === 'staff') return a.name ? `Rethink · ${a.name}` : 'Rethink';
  return a.name ?? 'Your team';
}
